<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <!-- Conectores SiteUP — mesmo padrão de card das integrações -->
          <IntegrationItem
            id="meta_connection"
            route-name="settings_integrations_meta"
            icon-emoji="📱"
            :show-status="false"
            name="Automação de comentários (Instagram e Facebook)"
            description="Vincule sua Página do Facebook uma vez (por ID) e habilite as automações de comentário em DM no Instagram e no Facebook. A conta de Instagram vinculada à Página entra junto."
          />
          <IntegrationItem
            id="lead_capture"
            route-name="settings_integrations_lead_capture"
            icon-emoji="🔗"
            :show-status="false"
            name="Gerador de Link de Captura"
            description="Cria links e webhooks que capturam gclid, fbclid, UTMs e iniciam conversas no WhatsApp já com o lead pré-cadastrado. Para Google Ads, Meta CTWA ou forms externos (Tally, Typeform, n8n, Hubspot)."
          />
          <IntegrationItem
            id="meta_lead_forms"
            route-name="settings_integrations_meta_lead_forms"
            icon-emoji="📝"
            :show-status="false"
            name="Captura de Leads (Formulários Meta)"
            description="Recebe os leads dos formulários do Meta (Lead Ads): salva respostas em campos personalizados, manda mensagem, qualifica e (opcional) deixa a IA continuar. Métricas por formulário."
          />
          <IntegrationItem
            id="purchase_integrations"
            route-name="settings_integrations_purchases"
            icon-emoji="🛒"
            :show-status="false"
            :name="$t('INTEGRATION_SETTINGS.PURCHASE.CARD_TITLE')"
            :description="$t('INTEGRATION_SETTINGS.PURCHASE.CARD_DESCRIPTION')"
          />
          <!-- Integrações padrão -->
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-10 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
      </div>
    </template>
  </SettingsLayout>
</template>
